/**
 * Renders an empty page that contains the logic that handles the final logic of
 * the Sign in with Apple flow.
 *
 * These are the steps required to get to this final part of the Sign in with
 * Apple flow:
 * 1. User asks to execute an action related to the Sign in with Apple flow in
 *    ka.org (e.g. logging in with Apple, signing up with Apple, re-auth or
 *    linking an Apple account).
 * 2. The `encodeAppleLoginState` mutation is called to generate some data that
 *    will be verified once the user is successfully authenticated with their
 *    Apple credentials.
 * 3. The Sign in with Apple flow is started by redirecting to
 *    appleid.apple.com.
 * 4. After the log in is succeeded, the user is redirected back to
 *    ka.org/login/apple/complete, where we verify the request authenticity, by
 *    decoding the Apple login state and obtaining the access_token from the
 *    auth_code generated by Apple.
 * 5. Then we get redirected to this page.
 *
 * NOTE(juan): This route should only be used with the Mobile Web and Mobile App
 * flows (Android and iOS 12<). This is needed in these cases because we are not
 * allowed to use the pop-up window with the devices previously mentioned as
 * part of the Sign in with Apple flow.
 *
 * ## Examples
 *
 * Some examples of valid uses of this route:
 * - /login/apple/redirect?continue=/math
 * - /login/apple/redirect?continue=/math&isFirstLogin=1
 * - /login/apple/redirect?continue=/math&signupCodes=ABC123
 *
 */
import * as React from "react";
import {Navigate} from "react-router-dom-v5-compat";

import {
    calculateNextUrl,
    joinClassroomsPostlogin,
    type JoinClassroomsData,
    type RedirectOptions,
} from "@khan/auth-shared";
import FullPageSpinner from "@khan/component-screens/full-page-spinner";
import {useGql, type GqlFetchFn} from "@khan/data-gql";
import {PermissionsInfo} from "@khan/shared-core";
import {useLocation} from "@khan/urls";

interface JoinClassroomMutationProps {
    signupCodes: Array<string>;
    children: (
        joinClassrooms?: JoinClassroomsData | null | undefined,
    ) => React.ReactNode;
    gqlFetch: GqlFetchFn;
}

interface AppleRedirectProps {
    redirectOptions: RedirectOptions;
}

/**
 * Wrapper component that handles the joinClassroomsPostlogin mutation
 */
const JoinClassroomMutation = ({
    signupCodes,
    children,
    gqlFetch,
}: JoinClassroomMutationProps) => {
    const [joinClassroomsResult, setJoinClassroomsResult] = React.useState<
        JoinClassroomsData | null | undefined
    >(null);

    React.useEffect(() => {
        joinClassroomsPostlogin(gqlFetch, signupCodes).then(
            (joinClassrooms) => {
                setJoinClassroomsResult(joinClassrooms);
            },
        );
    }, [gqlFetch, signupCodes]);

    return children(joinClassroomsResult);
};

/**
 * Wrapper component that handles the computeNextUrl call
 */
const AppleRedirect = ({redirectOptions}: AppleRedirectProps) => {
    const [nextUrl, setNextUrl] = React.useState<string | null | undefined>(
        null,
    );

    React.useEffect(() => {
        calculateNextUrl(redirectOptions).then((url) => {
            setNextUrl(url);
        });
    }, [redirectOptions]);

    if (nextUrl == null) {
        return <FullPageSpinner />;
    }
    return <Navigate to={nextUrl} />;
};

/**
 * Main Redirect Page component
 */
export default function AppleRedirectWrapper() {
    const location = useLocation();
    const gqlFetch = useGql();

    if (!PermissionsInfo.isLoggedIn || !location.queryParams.continue) {
        return <Navigate to="/" />;
    }

    const {
        continue: continueUrl,
        signupCodes,
        isFirstLogin,
    } = location.queryParams;
    const isFirstLoginBool = isFirstLogin === "1";

    const redirectOptions: RedirectOptions = {
        gqlFetch,
        location,
        loginType: "apple" as const,
        continueUrl,
        isFirstLogin: isFirstLoginBool,
    };

    if (signupCodes) {
        const signupCodesList = signupCodes.split(",");
        return (
            <JoinClassroomMutation
                gqlFetch={gqlFetch}
                signupCodes={signupCodesList}
            >
                {(joinClassrooms) => {
                    if (!joinClassrooms) {
                        return <FullPageSpinner />;
                    }
                    return (
                        <AppleRedirect
                            redirectOptions={{
                                ...redirectOptions,
                                joinClassrooms,
                            }}
                        />
                    );
                }}
            </JoinClassroomMutation>
        );
    }

    return <AppleRedirect redirectOptions={redirectOptions} />;
}
